import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { API } from 'utilities/api';
import PlatformLanding from './PlatformLanding';
import ComingSoon from './ComingSoon';
import PublicSite from './PublicSite';

/**
 * Host-based root dispatcher.
 *
 * Mounted at "/". Asks the backend (`GET /public/site/route`) what the
 * current Host header resolves to and renders the right shell:
 *
 *   - Platform host (no brand match)  → <PlatformLanding />
 *   - Brand domain, no rule mapped    → <ComingSoon />
 *   - Brand domain, target=page       → <PublicSite brandRef pageSlug />
 *   - Brand domain, target=webapp     → <PublicSite brandRef /> (brand homepage)
 *
 * Seeds `window.__TPO_BRAND__` so ComingSoon / nested storefront pages
 * can read the brand on first paint without a second round-trip.
 */
/**
 * Swap the tab icon for one derived from the brand.
 *
 * index.html ships TechProOne's favicon, so without this a brand's own
 * domain flies the platform's logo in the tab — the same leak as the static
 * <title>, just in image form.
 *
 * The route envelope carries no logo URL, only name and colour, so the icon
 * is drawn here: the brand's initial on its accent colour. That is a
 * deliberate trade against waiting for a real logo from a second request —
 * a neutral mark is strictly better than another company's logo, and it
 * costs no round trip.
 */
const applyBrandFavicon = (brand) => {
  // An uploaded icon wins: it is the brand's actual mark, and the drawn
  // letter below only ever existed because there was nowhere to upload one.
  if (brand?.favicon_upload_path) {
    try {
      document
        .querySelectorAll("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
        .forEach((el) => el.parentNode.removeChild(el));
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = brand.favicon_upload_path;
      document.head.appendChild(link);
    } catch (_) { /* icon is cosmetic */ }
    return;
  }

  const initial = (brand?.name || '').trim().charAt(0).toUpperCase();
  if (!initial) return;
  const bg = brand.brand_color || '#4F46E5';
  const svg = [
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>",
    `<rect width='64' height='64' rx='14' fill='${bg}'/>`,
    "<text x='50%' y='50%' dy='.35em' text-anchor='middle' fill='#fff'",
    " font-family='Inter,Helvetica,Arial,sans-serif' font-size='38'",
    ` font-weight='700'>${initial}</text>`,
    '</svg>',
  ].join('');
  const href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  try {
    document
      .querySelectorAll("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
      .forEach((el) => el.parentNode.removeChild(el));
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.href = href;
    document.head.appendChild(link);
  } catch (_) { /* icon is cosmetic — never break the page for it */ }
};

export default function BrandHostRouter() {
  const [state, setState] = useState({ phase: 'loading', payload: null });

  useEffect(() => {
    let cancelled = false;
    API.publicResolvedRoute()
      .then((res) => {
        if (cancelled) return;
        if (!res || !res.brand || !res.route) {
          // Backend returned 200 but with no envelope — treat as platform.
          setState({ phase: 'platform', payload: null });
          return;
        }
        try { window.__TPO_BRAND__ = res.brand; } catch (_) { /* ignore */ }
        // The tab said "TechProOne" on every brand domain, because that is the
        // static <title> in index.html and nothing ever replaced it. A visitor
        // on the brand's own domain should never see the platform's name —
        // this is the only point where the brand for this host is known, so
        // the rename belongs here rather than in each leaf page.
        if (res.brand?.name) {
          try { document.title = res.brand.name; } catch (_) { /* ignore */ }
          applyBrandFavicon(res.brand);
        }
        setState({ phase: 'brand', payload: res });
      })
      .catch(() => {
        // 404 / network error → fall back to platform landing.
        if (cancelled) return;
        setState({ phase: 'platform', payload: null });
      });
    return () => { cancelled = true; };
  }, []);

  if (state.phase === 'loading') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0b0d1a',
          color: '#9ca3af',
          fontFamily: "'Inter', -apple-system, sans-serif",
        }}
      >
        Loading…
      </div>
    );
  }

  if (state.phase === 'platform') {
    return <PlatformLanding />;
  }

  const { brand, route } = state.payload;
  if (route.type === 'coming_soon') {
    return <ComingSoon />;
  }
  /**
   * A host that serves the brand's own admin, e.g. admin.their-domain.com.
   *
   * The dashboard is already white-labelled by host — it scopes itself to the
   * one brand and drops every TechProOne mark — but routing could only point
   * a host at the public site, so the only way in was `/dashboard` on the
   * same host visitors are on. Sending the root here hands the whole host to
   * the dashboard's own router, which then owns every path under it.
   */
  if (route.type === 'dashboard') {
    return <Navigate to='/dashboard' replace />;
  }
  if (route.type === 'page') {
    return (
      <PublicSite
        brandRef={brand._id}
        pageSlug={route.page?.slug}
      />
    );
  }
  // 'webapp' → render the brand's homepage as the SPA's root.
  return <PublicSite brandRef={brand._id} />;
}
